import gsap from 'gsap';
import * as Rellax from 'rellax';

/**
 * Animate the provided image element randomly.
 *
 * @param  {HTMLImageElement} hex
 * @returns {GSAPTimeline} The GSAP timeline object
 */
function animatieHexagon(hex: HTMLImageElement): GSAPTimeline {
  return gsap.timeline().to(hex, {
    x: '+=random(-150, 150, 10)',
    y: '+=random(-150, 150, 10)',
    alpha: 'random(0.85, 1)',
    duration: 3,
    ease: 'none',
    repeat: -1,
    repeatRefresh: true,
  });
}

/**
 * Clones the backdrop layer to match the document height.
 * If animation is disabled the clone count and offsets are adjusted
 *
 * @param  {HTMLElement} layerWrapper
 * @param  {boolean} disableAnimation
 * @returns Array
 */
function cloneLayersBasedOnDocumentHeight(
  layerWrapper: HTMLElement,
  disableAnimation: boolean,
): Array<HTMLElement> {
  // Create a clone count modifier based on weither animations are used
  const cloneModifier = disableAnimation ? 0 : 1;

  // Get data to calculate the ammount of clones needed
  const documentHeight = document.body.scrollHeight;
  const viewPortHeight = window.innerHeight;
  // Set clone count
  const duplicationCount = Math.ceil(documentHeight / viewPortHeight);

  // Create a place to store all layer wrappers
  // Also store the initial one, the return array is used
  // for animations later on
  const clones = [layerWrapper];

  // Loop based on the clone count requested
  for (let i = 0; i < duplicationCount - cloneModifier; i += 1) {
    // Create a clone of the layer wrapper element
    const clone = layerWrapper.cloneNode(true) as HTMLElement;
    // Append it to the DOM
    layerWrapper.parentElement.append(clone);

    // Offset the wrapper so they don't stack
    layerWrapper.style.transform = `translateY(${viewPortHeight * (i + 2)}px)`;

    // Add it to the clone array
    clones.push(clone);
  }

  // Return the array of all created clones and the original,
  // Returned array is used for animation.
  return clones;
}

/**
 * Initialize the backdrop animation with the 'floating' hexagons.
 * Animations can be disabled during development or if user prefers it.
 *
 * If animations are disabled, this script just spreads around the hexagons
 *
 * @param  {boolean=false} disableAnimation
 */
function initBackDropWithImages(disableAnimation: boolean = false) {
  disableAnimation = true;
  // Get the backdrop element
  const backdropElement = document.querySelector('.backdrop') as HTMLElement;

  // Set it's height to match the body,
  // allowing the use of hidden overflow
  backdropElement.style.height = `${document.body.scrollHeight}px`;

  // Get the layer wrapper created in the footer
  const layerWrapper = document.querySelector('.backdrop__layer-wrapper') as HTMLElement;

  // Clone it to match the body height,
  // and store all layer wrappers in the variable
  const layerWrappers = cloneLayersBasedOnDocumentHeight(layerWrapper, disableAnimation);

  // Loop over all wrappers to adjust images per wrapper
  layerWrappers.forEach((wrapper) => {
    // Get all iamges
    const images = wrapper.querySelectorAll('.backdrop__image') as NodeListOf<HTMLImageElement>;

    // Define the offsets to move images to the correct spots
    const offsets = [
      {
        x: -200,
        y: -200,
      },
      {
        x: -300,
        y: 500,
      },
      {
        x: 440,
        y: -440,
      },
    ];

    // Loop over all images to position and animate them
    images.forEach((image, index) => {
      // Apply minor randomization to the offsets
      const xOff = offsets[index].x + gsap.utils.random(-100, 100, 10);
      const yOff = offsets[index].y + gsap.utils.random(-100, 100, 10);

      // Set the offsets
      image.style.left = `${xOff}px`;
      image.style.top = `${yOff}px`;

      if (!disableAnimation) {
        // Animate the images infinitly
        animatieHexagon(image);

        // Define the calucation for parallax speed
        const speedCalculation = (10 * (index * 6 + 1)) / 20;

        // Add parallax scrolling to the hexagon group
        new Rellax(image.parentElement, {
          speed: speedCalculation,
          center: false,
        });
      }
    });
  });

  // Change backdrop height when resizing window
  // Doens't happen much, so no need to rebuild the logic
  window.addEventListener('resize', () => {
    backdropElement.style.height = `${document.body.scrollHeight}px`;
  });

  // create an Observer instance
  const resizeObserver = new ResizeObserver((entries) => {
    backdropElement.style.height = `${document.body.scrollHeight}px`;
  });

  // start observing a DOM node
  resizeObserver.observe(document.body);
}

export default initBackDropWithImages;
